import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiTrash2, FiMove, FiInfo } from 'react-icons/fi';
import './ExerciseEditor.css';

const ExerciseEditor = ({ exercise, onUpdate, onDelete, blockType }) => {
  const [expanded, setExpanded] = useState(false);

  // Detect if this is a cardio exercise
  const isCardio = exercise.category === 'aerobic' ||
    ['treadmill', 'stationary_bike', 'assault_bike', 'rowing_machine', 'ergo_ski',
     'elliptical', 'other_cardio', 'bike', 'pool', 'jump_rope', 'stair_climber',
     'wattbike', 'concept2_rower', 'skierg', 'versaclimber'].includes(exercise.equipment);

  // Detect if this exercise is part of a circuit block
  const isCircuit = ['superset', 'triset', 'multiset'].includes(blockType);

  const handleMetricChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  return (
    <div className="exercise-editor">
      <div className="exercise-header" onClick={() => setExpanded(!expanded)}>
        <div className="exercise-header-left">
          <button className="drag-handle" title="Drag to reorder">
            <FiMove />
          </button>
          <div className="exercise-name-section">
            <span className="exercise-name">{exercise.exerciseName}</span>
            <span className="exercise-equipment">{exercise.equipment}</span>
          </div>
          <div className="exercise-quick-view">
            {isCardio ? (
              <>
                {!isCircuit && exercise.primaryMetrics.sets && (
                  <span>{exercise.primaryMetrics.sets} × </span>
                )}
                <span>{exercise.primaryMetrics.duration || 10}min</span>
                {exercise.primaryMetrics.intensity && (
                  <span> @ {exercise.primaryMetrics.intensity}</span>
                )}
                {!isCircuit && exercise.primaryMetrics.rest > 0 && (
                  <span> | {exercise.primaryMetrics.rest}s rest</span>
                )}
              </>
            ) : (
              <>
                {!isCircuit && exercise.primaryMetrics.sets && (
                  <span>{exercise.primaryMetrics.sets} × </span>
                )}
                <span>{exercise.primaryMetrics.reps}</span>
                {exercise.primaryMetrics.weight && (
                  <span> @ {exercise.primaryMetrics.weight}kg</span>
                )}
                {!isCircuit && (
                  <span> | {exercise.primaryMetrics.rest}s rest</span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="exercise-header-right">
          <button
            className="toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          <button
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <FiTrash2 />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="exercise-details">
          <div className="metrics-grid">
            {/* Sets - only show for non-circuit exercises */}
            {!isCircuit && (
              <div className="metric-field">
                <label>Sets</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={exercise.primaryMetrics.sets || 3}
                  onChange={(e) => handleMetricChange('sets', parseInt(e.target.value))}
                />
              </div>
            )}

            {isCardio ? (
              <>
                {/* Duration (for cardio) */}
                <div className="metric-field">
                  <label>Duration (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={exercise.primaryMetrics.duration || 10}
                    onChange={(e) => handleMetricChange('duration', parseInt(e.target.value))}
                  />
                </div>

                {/* Intensity (for cardio) */}
                <div className="metric-field">
                  <label>Intensity</label>
                  <select
                    value={exercise.primaryMetrics.intensity || 'moderate'}
                    onChange={(e) => handleMetricChange('intensity', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="moderate">Moderate</option>
                    <option value="moderate_high">Moderate-High</option>
                    <option value="high">High</option>
                    <option value="maximum">Maximum</option>
                  </select>
                </div>

                {/* Rest between sets (for cardio) - only show for non-circuit */}
                {!isCircuit && (
                  <div className="metric-field">
                    <label>Rest (seconds)</label>
                    <input
                      type="number"
                      min="0"
                      max="600"
                      step="15"
                      value={exercise.primaryMetrics.rest || 0}
                      onChange={(e) => handleMetricChange('rest', parseInt(e.target.value))}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Reps (for strength) */}
                <div className="metric-field">
                  <label>Reps</label>
                  <input
                    type="text"
                    value={exercise.primaryMetrics.reps}
                    onChange={(e) => handleMetricChange('reps', e.target.value)}
                    placeholder="e.g., 8-12 or 10"
                  />
                </div>

                {/* Weight (for strength) */}
                <div className="metric-field">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={exercise.primaryMetrics.weight || ''}
                    onChange={(e) => handleMetricChange('weight', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="Optional"
                  />
                </div>

                {/* Rest (for strength) - only show for non-circuit */}
                {!isCircuit && (
                  <div className="metric-field">
                    <label>Rest (seconds)</label>
                    <input
                      type="number"
                      min="0"
                      max="300"
                      step="15"
                      value={exercise.primaryMetrics.rest || 60}
                      onChange={(e) => handleMetricChange('rest', parseInt(e.target.value))}
                    />
                  </div>
                )}

                {/* Tempo (for strength) */}
                <div className="metric-field">
                  <label>
                    Tempo
                    <span className="tooltip-icon">
                      <FiInfo />
                      <span className="tooltip-text">
                        Exercise speed/rhythm. Format: Eccentric-Pause-Concentric-Pause (e.g., 3-1-2-0 means 3 sec down, 1 sec pause, 2 sec up, no pause)
                      </span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={exercise.primaryMetrics.tempo || ''}
                    onChange={(e) => handleMetricChange('tempo', e.target.value)}
                    placeholder="e.g., 3-1-2-0"
                  />
                </div>

                {/* RPE (for strength) */}
                <div className="metric-field">
                  <label>
                    RPE (1-10)
                    <span className="tooltip-icon">
                      <FiInfo />
                      <span className="tooltip-text">
                        Rate of Perceived Exertion: How hard the exercise feels on a scale of 1-10. 1 = very easy, 10 = maximum effort
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={exercise.primaryMetrics.rpe || ''}
                    onChange={(e) => handleMetricChange('rpe', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Optional"
                  />
                </div>

                {/* RIR (for strength) */}
                <div className="metric-field">
                  <label>
                    RIR (0-5)
                    <span className="tooltip-icon">
                      <FiInfo />
                      <span className="tooltip-text">
                        Reps in Reserve: How many more reps could be done before failure. 0 = at failure, 3 = 3 more reps possible
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={exercise.primaryMetrics.rir || ''}
                    onChange={(e) => handleMetricChange('rir', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Optional"
                  />
                </div>
              </>
            )}

            {/* Duration (for time-based exercises) */}
            {exercise.measurementType === 'time' && (
              <div className="metric-field">
                <label>Duration (seconds)</label>
                <input
                  type="number"
                  min="10"
                  value={exercise.primaryMetrics.duration || 60}
                  onChange={(e) => handleMetricChange('duration', parseInt(e.target.value))}
                />
              </div>
            )}

            {/* Distance (for cardio) */}
            {exercise.measurementType === 'distance' && (
              <div className="metric-field">
                <label>Distance (meters)</label>
                <input
                  type="number"
                  min="100"
                  value={exercise.primaryMetrics.distance || 1000}
                  onChange={(e) => handleMetricChange('distance', parseInt(e.target.value))}
                />
              </div>
            )}

            {/* Heart Rate Zone */}
            {(exercise.category === 'aerobic' || exercise.measurementType === 'cardio') && (
              <div className="metric-field">
                <label>HR Zone (1-5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={exercise.primaryMetrics.heartRateZone || ''}
                  onChange={(e) => handleMetricChange('heartRateZone', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Optional"
                />
              </div>
            )}

            {/* Watts (for specialized equipment) */}
            {(exercise.equipment === 'cardio_equipment' || exercise.subcategory === 'specialized') && (
              <div className="metric-field">
                <label>Watts</label>
                <input
                  type="number"
                  min="50"
                  value={exercise.primaryMetrics.watts || ''}
                  onChange={(e) => handleMetricChange('watts', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Optional"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="exercise-notes">
            <label>Exercise Notes / Coaching Cues</label>
            <textarea
              value={exercise.primaryMetrics.notes || ''}
              onChange={(e) => handleMetricChange('notes', e.target.value)}
              placeholder="Add notes, form cues, or modifications..."
              rows="3"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseEditor;
